'use client';

import {
  BottomSheetTitle,
  BottomSheetTitleBox,
  BottomSheetTitleContainer,
  Typography,
} from '@musinsa/mds';
import { useRouter, useSearchParams } from 'next/navigation';
import type { FC } from 'react';
import { Suspense, useEffect } from 'react';
import { getPaths } from '../../common';
import { AgreementContent } from '../components/AgreementContent';
import {
  AGREEMENT_UI_TEXT,
  AGREEMENT_VERSION_QUERY_KEY,
  type AgreementBrand,
  type AgreementType,
  getPreviousVersion,
} from '../utils/agreementVersions';

/** 무신사 공통 레이아웃은 main#commonLayoutContent 등이 스크롤 루트인 경우가 있어, Link 기본 스크롤만으로는 위치가 유지될 수 있음 */
const scrollCuratorAgreementViewsToTop = () => {
  if (typeof window === 'undefined') return;

  window.scrollTo(0, 0);

  for (const el of [
    document.getElementById('commonLayoutContent'),
    document.querySelector('main'),
    document.documentElement,
    document.body,
  ]) {
    if (el && 'scrollTop' in el) {
      (el as HTMLElement).scrollTop = 0;
    }
  }
};

interface AgreementPageProps {
  type: AgreementType;
  brand: AgreementBrand;
}

const AgreementPageContent = ({ type, brand }: AgreementPageProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const queryKey = AGREEMENT_VERSION_QUERY_KEY[type];
  const version = searchParams.get(queryKey) ?? undefined;

  const paths = getPaths(brand);

  useEffect(() => {
    scrollCuratorAgreementViewsToTop();
    const id = window.requestAnimationFrame(() => {
      scrollCuratorAgreementViewsToTop();
    });
    return () => window.cancelAnimationFrame(id);
  }, [type]);

  const handlePreviousClick = () => {
    const prev = getPreviousVersion(type, brand, version);
    if (prev) {
      router.replace(paths[type]({ query: { [queryKey]: prev } }));
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <BottomSheetTitle>
        <BottomSheetTitleContainer className="px-4 py-3">
          <BottomSheetTitleBox>
            <Typography size="18" weight="medium">
              {AGREEMENT_UI_TEXT[type].title}
            </Typography>
          </BottomSheetTitleBox>
        </BottomSheetTitleContainer>
      </BottomSheetTitle>

      <div className="flex-1 overflow-y-auto bg-white">
        <div className="px-4 py-3">
          <AgreementContent
            type={type}
            brand={brand}
            version={version}
            onPreviousClick={handlePreviousClick}
          />
        </div>
      </div>
    </div>
  );
};

export const AgreementPage: FC<AgreementPageProps> = ({ type, brand }) => (
  <Suspense
    fallback={
      <Typography variant="body13pxReg" colorScheme="gray-600">
        약관을 불러오는 중입니다.
      </Typography>
    }
  >
    <AgreementPageContent type={type} brand={brand} />
  </Suspense>
);
